import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
  Divider,
  FormErrorMessage,
  Checkbox,
  Button,
  Box,
  Input,
  useClipboard,
  Code,
} from "@chakra-ui/react";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import { FloatingField } from "design-system";
import { SSOProviderConfig, SSOProviderFormValues } from "./models";
import {
  useCreateSSOProvider,
  useUpdateSSOProvider,
  useGenerateSCIMToken,
  useDisableSCIM,
  useParseMetadata,
} from "./hooks";

interface SSOSettingsFormProps {
  ssoProvider?: SSOProviderConfig | null;
  onClose: () => void;
}

// Validation schema
const ssoProviderSchema = yup.object().shape({
  provider: yup.string().required("Provider is required"),
  protocol: yup.string().required("Protocol is required"),

  // SAML validations
  ssoUrl: yup.string().when("protocol", {
    is: "saml",
    then: (schema) => schema.required("SSO URL is required for SAML").url("Must be a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),
  issuer: yup.string().when("protocol", {
    is: (protocol: string) => protocol === "saml" || protocol === "oidc",
    then: (schema) => schema.required("Issuer is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  certificate: yup.string().when(["protocol", "metadataUrl"], {
    is: (protocol: string, metadataUrl: string) => protocol === "saml" && !metadataUrl,
    then: (schema) => schema.required("Certificate is required if Metadata URL is not provided"),
    otherwise: (schema) => schema.notRequired(),
  }),
  metadataUrl: yup.string().when("protocol", {
    is: "saml",
    then: (schema) => schema.url("Must be a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // OIDC validations
  clientId: yup.string().when("protocol", {
    is: "oidc",
    then: (schema) => schema.required("Client ID is required for OIDC"),
    otherwise: (schema) => schema.notRequired(),
  }),
  clientSecret: yup.string().when("protocol", {
    is: "oidc",
    then: (schema) => schema.required("Client Secret is required for OIDC"),
    otherwise: (schema) => schema.notRequired(),
  }),
  discoveryUrl: yup.string().when("protocol", {
    is: "oidc",
    then: (schema) => schema.url("Must be a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Policy fields
  enforced: yup.boolean(),
  allowedDomains: yup.string(),
  autoProvisionUsers: yup.boolean(),
});

export default function SSOSettingsForm({ ssoProvider, onClose }: SSOSettingsFormProps) {
  const isEditing = !!ssoProvider;
  const createMutation = useCreateSSOProvider();
  const updateMutation = useUpdateSSOProvider();
  const generateSCIMTokenMutation = useGenerateSCIMToken();
  const disableSCIMMutation = useDisableSCIM();
  const parseMetadataMutation = useParseMetadata();

  const [scimToken, setScimToken] = useState<string | null>(null);
  const [scimBaseUrl, setScimBaseUrl] = useState<string | null>(null);
  const [showManualConfig, setShowManualConfig] = useState(false);
  const { hasCopied: hasTokenCopied, onCopy: onTokenCopy } = useClipboard(scimToken || "");
  const { hasCopied: hasUrlCopied, onCopy: onUrlCopy } = useClipboard(scimBaseUrl || "");

  const defaultValues = useMemo<Partial<SSOProviderFormValues>>(
    () => ({
      provider: ssoProvider?.provider || "okta",
      protocol: ssoProvider?.protocol || "saml",
      ssoUrl: ssoProvider?.ssoUrl || "",
      issuer: ssoProvider?.issuer || "",
      metadataUrl: ssoProvider?.metadataUrl || "",
      discoveryUrl: ssoProvider?.discoveryUrl || "",
      enforced: ssoProvider?.enforced || false,
      allowedDomains: ssoProvider?.allowedDomains?.join("\n") || "",
      autoProvisionUsers: ssoProvider?.autoProvisionUsers || false,
      certificate: ssoProvider?.certificate || "",
      clientId: "",
      clientSecret: "",
    }),
    [ssoProvider],
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<SSOProviderFormValues>({
    resolver: yupResolver(ssoProviderSchema),
    defaultValues,
    mode: "onBlur",
  });

  const protocol = watch("protocol");
  const metadataUrl = watch("metadataUrl");
  const discoveryUrl = watch("discoveryUrl");

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data: SSOProviderFormValues) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
    onClose();
  };

  const handleGenerateSCIMToken = async () => {
    const response = await generateSCIMTokenMutation.mutateAsync();
    setScimToken(response.token);
    setScimBaseUrl(response.scimBaseUrl);
  };

  const handleDisableSCIM = async () => {
    await disableSCIMMutation.mutateAsync();
    setScimToken(null);
    setScimBaseUrl(null);
  };

  const handleFetchMetadata = async () => {
    // Use the appropriate URL field based on protocol
    const urlToFetch = protocol === "oidc" ? discoveryUrl : metadataUrl;
    if (!urlToFetch) return;

    try {
      const response = await parseMetadataMutation.mutateAsync({
        metadataUrl: urlToFetch,
        protocol,
      });

      // Auto-populate fields based on protocol
      if (protocol === "saml") {
        if (response.issuer) setValue("issuer", response.issuer);
        if (response.ssoUrl) setValue("ssoUrl", response.ssoUrl);
        if (response.certificate) setValue("certificate", response.certificate);
      } else if (protocol === "oidc") {
        if (response.issuer) setValue("issuer", response.issuer);
        if (response.discoveryUrl) setValue("discoveryUrl", response.discoveryUrl);
      }
    } catch {
      // Error toast is handled by the mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Provider Selection */}
        <FormControl isInvalid={!!errors.provider}>
          <FormLabel>SSO Provider</FormLabel>
          <Select {...register("provider")} disabled={isLoading}>
            <option value="okta">Okta</option>
            <option value="azure_ad">Azure AD</option>
            <option value="google_workspace">Google Workspace</option>
            <option value="onelogin">OneLogin</option>
          </Select>
          <FormErrorMessage>{errors.provider?.message}</FormErrorMessage>
        </FormControl>

        {/* Protocol Selection */}
        <FormControl isInvalid={!!errors.protocol}>
          <FormLabel>Protocol</FormLabel>
          <Select {...register("protocol")} disabled={isLoading || isEditing}>
            <option value="saml">SAML 2.0</option>
            <option value="oidc">OpenID Connect (OIDC)</option>
          </Select>
          {isEditing && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              Protocol cannot be changed after creation
            </Text>
          )}
          <FormErrorMessage>{errors.protocol?.message}</FormErrorMessage>
        </FormControl>

        <Divider />

        {/* SAML Configuration */}
        {protocol === "saml" && (
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="semibold">SAML Configuration</Text>
              <Button size="sm" variant="link" onClick={() => setShowManualConfig(!showManualConfig)}>
                {showManualConfig ? "Hide Manual Fields" : "Show Manual Fields"}
              </Button>
            </HStack>

            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={1} flex={1}>
                <AlertDescription fontSize="sm">
                  Enter your SAML metadata URL below and click &quot;Fetch Configuration&quot; to auto-populate the
                  fields.
                </AlertDescription>
                <AlertDescription fontSize="xs" color="gray.600">
                  Or configure manually using the fields below.
                </AlertDescription>
              </VStack>
            </Alert>

            <FormControl isInvalid={!!errors.metadataUrl}>
              <FormLabel>Metadata URL</FormLabel>
              <HStack>
                <Input
                  {...register("metadataUrl")}
                  disabled={isLoading}
                  placeholder="https://your-provider.com/app/sso/saml/metadata"
                />
                <Button
                  onClick={handleFetchMetadata}
                  isLoading={parseMetadataMutation.isPending}
                  isDisabled={!metadataUrl || isLoading}
                  colorScheme="blue"
                  flexShrink={0}
                >
                  Fetch Configuration
                </Button>
              </HStack>
              <FormErrorMessage>{errors.metadataUrl?.message}</FormErrorMessage>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Recommended: Fetch configuration automatically from your IdP&apos;s metadata URL
              </Text>
            </FormControl>

            {showManualConfig && (
              <>
                <Divider />
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Manual Configuration (Advanced)
                </Text>

                <FloatingField name="ssoUrl" errors={errors.ssoUrl?.message}>
                  <FloatingField.Label>SSO URL</FloatingField.Label>
                  <FloatingField.Text control={control} name="ssoUrl" disabled={isLoading} />
                </FloatingField>

                <FloatingField name="issuer" errors={errors.issuer?.message}>
                  <FloatingField.Label>Issuer (IdP Entity ID)</FloatingField.Label>
                  <FloatingField.Text control={control} name="issuer" disabled={isLoading} />
                </FloatingField>

                <FormControl isInvalid={!!errors.certificate}>
                  <FormLabel>Certificate</FormLabel>
                  <Textarea
                    {...register("certificate")}
                    disabled={isLoading}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    rows={6}
                    fontFamily="monospace"
                    fontSize="sm"
                  />
                  <FormErrorMessage>{errors.certificate?.message}</FormErrorMessage>
                </FormControl>
              </>
            )}
          </VStack>
        )}

        {/* OIDC Configuration */}
        {protocol === "oidc" && (
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="semibold">OIDC Configuration</Text>
              <Button size="sm" variant="link" onClick={() => setShowManualConfig(!showManualConfig)}>
                {showManualConfig ? "Hide Manual Fields" : "Show Manual Fields"}
              </Button>
            </HStack>

            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={1} flex={1}>
                <AlertDescription fontSize="sm">
                  Enter your OIDC issuer URL or discovery endpoint and click &quot;Fetch Configuration&quot; to
                  auto-populate.
                </AlertDescription>
                <AlertDescription fontSize="xs" color="gray.600">
                  Note: Client ID and Secret must be obtained from your IdP&apos;s admin console.
                </AlertDescription>
              </VStack>
            </Alert>

            <FormControl isInvalid={!!errors.discoveryUrl}>
              <FormLabel>Discovery URL or Issuer URL</FormLabel>
              <HStack>
                <Input
                  {...register("discoveryUrl")}
                  disabled={isLoading}
                  placeholder="https://dev-123.okta.com/.well-known/openid-configuration"
                />
                <Button
                  onClick={handleFetchMetadata}
                  isLoading={parseMetadataMutation.isPending}
                  isDisabled={!watch("discoveryUrl") || isLoading}
                  colorScheme="blue"
                  flexShrink={0}
                >
                  Fetch Configuration
                </Button>
              </HStack>
              <FormErrorMessage>{errors.discoveryUrl?.message}</FormErrorMessage>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enter either the full discovery URL or just the issuer URL (e.g., https://dev-123.okta.com)
              </Text>
            </FormControl>

            {showManualConfig && (
              <>
                <Divider />
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Manual Configuration (Advanced)
                </Text>

                <FloatingField name="issuer" errors={errors.issuer?.message}>
                  <FloatingField.Label>Issuer URL</FloatingField.Label>
                  <FloatingField.Text control={control} name="issuer" disabled={isLoading} />
                </FloatingField>
              </>
            )}

            <FloatingField name="clientId" errors={errors.clientId?.message}>
              <FloatingField.Label>Client ID (required)</FloatingField.Label>
              <FloatingField.Text
                control={control}
                name="clientId"
                disabled={isLoading}
                type={isEditing && ssoProvider?.hasClientId ? "password" : "text"}
              />
            </FloatingField>

            <FloatingField name="clientSecret" errors={errors.clientSecret?.message}>
              <FloatingField.Label>Client Secret (required)</FloatingField.Label>
              <FloatingField.Text control={control} name="clientSecret" disabled={isLoading} type="password" />
            </FloatingField>

            {isEditing && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Leave Client ID and Secret blank to keep existing values. Only provide new values to update them.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        )}

        <Divider />

        {/* Policy Configuration */}
        <VStack spacing={4} align="stretch">
          <Text fontWeight="semibold">SSO Enforcement Policy</Text>

          <FormControl>
            <Checkbox {...register("enforced")} disabled={isLoading}>
              Enforce SSO for all users
            </Checkbox>
            <Text fontSize="sm" color="gray.500" mt={1}>
              When enabled, users must sign in via SSO
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel>Allowed Email Domains (one per line)</FormLabel>
            <Textarea
              {...register("allowedDomains")}
              disabled={isLoading}
              placeholder="example.com&#10;company.com"
              rows={3}
            />
            <Text fontSize="sm" color="gray.500" mt={1}>
              Leave empty to allow all domains. SSO will only be enforced for listed domains.
            </Text>
          </FormControl>

          <FormControl>
            <Checkbox {...register("autoProvisionUsers")} disabled={isLoading}>
              Auto-provision new users
            </Checkbox>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Automatically create accounts for new users who sign in via SSO (assigned &quot;Basic&quot; role)
            </Text>
          </FormControl>
        </VStack>

        {/* SCIM Provisioning Section - Only show when editing */}
        {isEditing && (
          <>
            <Divider />

            <VStack spacing={4} align="stretch">
              <Text fontWeight="semibold">SCIM Provisioning</Text>

              {!ssoProvider?.scimEnabled && !scimToken ? (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Enable SCIM (System for Cross-domain Identity Management) to automatically provision and deprovision
                    users from your identity provider.
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={handleGenerateSCIMToken}
                    isLoading={generateSCIMTokenMutation.isPending}
                    size="sm"
                  >
                    Enable SCIM Provisioning
                  </Button>
                </Box>
              ) : (
                <VStack spacing={3} align="stretch">
                  <Alert status="success">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      SCIM provisioning is enabled
                      {ssoProvider?.scimConfiguredAt &&
                        ` (since ${new Date(ssoProvider.scimConfiguredAt).toLocaleDateString()})`}
                    </AlertDescription>
                  </Alert>

                  {/* Show token only when just generated */}
                  {scimToken && scimBaseUrl && (
                    <Alert status="warning">
                      <AlertIcon />
                      <VStack align="stretch" spacing={2} flex={1}>
                        <AlertDescription fontSize="sm" fontWeight="semibold">
                          Save these credentials now! The token will not be shown again.
                        </AlertDescription>

                        <FormControl>
                          <FormLabel fontSize="sm">SCIM Base URL</FormLabel>
                          <HStack>
                            <Input value={scimBaseUrl} isReadOnly size="sm" fontFamily="monospace" fontSize="xs" />
                            <Button onClick={onUrlCopy} size="sm" colorScheme={hasUrlCopied ? "green" : "gray"}>
                              {hasUrlCopied ? "Copied!" : "Copy"}
                            </Button>
                          </HStack>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">API Token</FormLabel>
                          <HStack>
                            <Input
                              value={scimToken}
                              isReadOnly
                              size="sm"
                              fontFamily="monospace"
                              fontSize="xs"
                              type="password"
                            />
                            <Button onClick={onTokenCopy} size="sm" colorScheme={hasTokenCopied ? "green" : "gray"}>
                              {hasTokenCopied ? "Copied!" : "Copy"}
                            </Button>
                          </HStack>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Copy and paste this token into your identity provider&apos;s SCIM configuration
                          </Text>
                        </FormControl>
                      </VStack>
                    </Alert>
                  )}

                  {/* Always show SCIM Base URL when enabled */}
                  {!scimToken && (
                    <FormControl>
                      <FormLabel fontSize="sm">SCIM Base URL</FormLabel>
                      <Code p={2} fontSize="xs" display="block">
                        {window.location.origin}
                        /api/v1/scim
                      </Code>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Use this URL in your identity provider&apos;s SCIM configuration
                      </Text>
                    </FormControl>
                  )}

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={handleGenerateSCIMToken}
                      isLoading={generateSCIMTokenMutation.isPending}
                      variant="outline"
                    >
                      Regenerate Token
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={handleDisableSCIM}
                      isLoading={disableSCIMMutation.isPending}
                    >
                      Disable SCIM
                    </Button>
                  </HStack>

                  <Text fontSize="xs" color="gray.500">
                    Note: Regenerating the token will invalidate the previous token. You&apos;ll need to update it in
                    your identity provider.
                  </Text>
                </VStack>
              )}
            </VStack>
          </>
        )}

        {/* Action Buttons */}
        <HStack mt={6} justify="end">
          <ProceedCancelButtons
            handleSubmit={handleSubmit(onSubmit)}
            onClose={onClose}
            proceedText={isEditing ? "Update" : "Create"}
            isLoading={isLoading}
            proceedDisabled={!isDirty || !isValid}
          />
        </HStack>
      </VStack>
    </form>
  );
}
